/**
 * 컬러 단일 출처 — tailwind.config.js 와 TS 코드(SVG fill·Emotion·inline style)가 함께 쓴다.
 *
 * 구조
 *   scale    : 계열별 50~900 hex. **hex 는 여기에만 존재한다.**
 *   semantic : 역할 토큰. scale 을 참조만 한다. Tailwind 클래스 이름이 된다
 *              (`text-body`, `bg-primary-soft`, `ring-line`, `text-warning-ink` …).
 *   color    : semantic 을 평평하게 편 것. SVG `fill={color.primary}` 처럼 JS 에서 쓴다.
 *
 * 규칙
 *   - 새 hex 를 코드에 직접 박지 않는다. 필요하면 scale 에 단계를 추가하고 semantic 에 역할을 단다.
 *   - 색을 고를 때는 역할(semantic) → 계열 스케일(`primary-300`) 순. 스케일 직접 사용은 예외.
 *   - 2026-09-05 해봄 브랜드 가이드 v3.0(2026.08) 팔레트 적용 — scale 만 바꿨고 semantic 키·코드는 그대로.
 *     앵커: Midnight Indigo #4338CA(indigo-500) · Star Amber #F59E0B(amber-400) · Deep Ink #0B0E27(gray-900)
 *           · Indigo Lite #A9BCFF(indigo-300) · (Paper #FBFAF7 은 앱에서 미사용 — gray-50 은 쿨 그레이 #F6F7FB, 2026-09-06). 나머지 단계는 OKLCH 사다리로 생성.
 *     비율: 인디고 60 · 페이퍼 30 · 앰버 10 — 앰버(accent)는 한 화면에 한 곳.
 *
 * CommonJS 인 이유: tailwind.config.js(CJS) 가 require 하고, TS 는 allowJs 로 import 한다.
 * 브라우저 API 사용 0 — prerender 안전.
 *
 * 문서: design/colors.md, design/students-first.md §9
 */

const scale = {
  // 메인 — 해봄 Midnight Indigo. 300·500 앵커, 나머지는 OKLCH 사다리(h 275~277).
  indigo: {
    50: "#F4F6FE",
    100: "#E8ECFE",
    200: "#D0D9FE",
    300: "#A9BCFF", // ★ Indigo Lite
    400: "#6772E3",
    500: "#4338CA", // ★ Midnight Indigo (주색)
    600: "#392CB2",
    700: "#2E2298",
    800: "#241A7C",
    900: "#18105B",
  },
  // 중립 — 전 단계 h 275(Deep Ink 와 같은 색상)의 쿨 그레이. 2026-09-06 Paper(50, h 91) 를 버리고 50 색온도 전환 +
  //   100~500 채도 한 단 상향 + 600 명도 소폭 하향(새 바탕 위 AA 4.67:1 유지). 700~900 은 사실상 동일. 근거: design/colors.md §3.
  gray: {
    50: "#F6F7FB", // 쿨 그레이 바탕. 브랜드 Paper #FBFAF7(h 91) 대신 2026-09-06 사용자 결정으로 h 275 — 인디고·회색과 색온도 통일
    100: "#EEF0F9",
    200: "#E0E3EE",
    300: "#CED1DE",
    400: "#B4B7C3",
    500: "#9598A4",
    600: "#6C6F7C",
    700: "#545764",
    800: "#313444",
    900: "#0B0E27", // ★ Deep Ink (본문·배경)
  },
  // 오류 — 브랜드 가이드에 없어 새로 잡음. h 25(주홍 쪽 빨강). 500 이 흰 배경 4.5:1 을 넘도록 L 0.56.
  red: {
    50: "#FFF2F0",
    100: "#FEDBD7",
    200: "#FEBAB4",
    300: "#FE8B83",
    400: "#F05653",
    500: "#D4212D",
    600: "#B60020",
    700: "#99001C",
    800: "#7C0017",
    900: "#5C0012",
  },
  // 강조·경고 — 해봄 Star Amber 한 계열. 강조는 400 채움, 경고는 50 배경 + 800 텍스트 짝(students-first §9 R5).
  amber: {
    50: "#FFF5E8",
    100: "#FFE7CA",
    200: "#FFD6A5",
    300: "#FFBC67",
    400: "#F59E0B", // ★ Star Amber (강조 · 한 화면에 한 곳)
    500: "#D68107",
    600: "#BB6802",
    700: "#9B540E",
    800: "#7B410F",
    900: "#5D2F0E",
  },
  // 정상 — 브랜드 가이드에 없어 새로 잡음. h 170(청록 쪽 초록). 3형 색각에서 메인과 ΔE 35 이상 확보(R6).
  // 심우주 남색 — 체험하기 요원증(`PlayAgentCard`) 한 곳에서만 쓴다.
  //   브랜드 Deep Ink(gray-900 #0B0E27) 보다 더 깊고 더 푸른 쪽이라 기존 스케일로 대체되지 않는다.
  //   ★ 역할 토큰(semantic)으로 올리지 않는다 — 올리면 Tailwind 전역 클래스가 되어 부스 전용 색이
  //     제품 전체의 선택지로 새어 나간다. 스케일 직접 사용은 팔레트 규칙이 허용하는 예외다.
  space: {
    100: "#232B63", // 카드 왼쪽 위 광원
    200: "#1A2B5E", // 카드 오른쪽 위 광원
    300: "#182055", // 증명사진 칸 위쪽
    400: "#0D1231", // 본체 상단
    500: "#0B1030", // 휘장 원판
    600: "#0A0E2A", // 증명사진 칸 아래쪽
    700: "#080B22", // 본체 중단
    800: "#06081A", // 본체 하단
    900: "#04061A", // 목걸이 슬롯 구멍
  },
  green: {
    50: "#E9FAF2",
    100: "#CFF1E3",
    200: "#A6E4CD",
    300: "#73D0B1",
    400: "#3BB693",
    500: "#049675",
    600: "#04755B",
    700: "#035E49",
    800: "#014937",
    900: "#003427",
  },
};

const white = "#FFFFFF";

/**
 * 역할 토큰. 키 이름이 곧 Tailwind 클래스 접미사다.
 *
 * 중립(텍스트·표면·경계선)
 *   text-heading / text-strong / text-body / text-muted / text-subtle / text-disabled
 *   bg-page / bg-fill / bg-fill-strong / bg-inverse
 *   border-line-faint / border-line-subtle / border-line / border-line-strong  (ring-, divide- 동일)
 *
 * 상태 역할 (각각 DEFAULT + hover/active + soft/muted + line + ink, 그리고 50~900 스케일)
 *   primary / secondary / accent / error / warning / success
 */
const semantic = {
  // ── 중립: 텍스트 ───────────────────────────────────────────
  heading: scale.gray[900], // 제목·숫자
  strong: scale.gray[800], // 강조 본문, 축 라벨
  body: scale.gray[700], // 본문 기본 (전체 최다 사용)
  muted: scale.gray[600], // 메타 정보
  subtle: scale.gray[500], // placeholder, 또래 평균 점선 (텍스트 금지)
  disabled: scale.gray[400], // 비활성 텍스트·아이콘·버튼 배경

  // ── 중립: 표면 ─────────────────────────────────────────────
  page: scale.gray[50], // ★ Paper. 페이지 바탕, 입력 배경
  fill: {
    DEFAULT: scale.gray[100], // 중립 칩·박스 배경
    strong: scale.gray[200], // 진한 중립 배경, 진행 바 트랙
  },
  inverse: scale.gray[900], // ★ Deep Ink. 다크 토스트·툴팁 배경 (글자는 white)

  // ── 중립: 경계선 ───────────────────────────────────────────
  line: {
    faint: scale.gray[50],
    subtle: scale.gray[100], // 카드 ring 기본
    DEFAULT: scale.gray[200], // 보더·divider·차트 그리드
    strong: scale.gray[300], // 입력 보더, 외곽 그리드
  },

  // ── 메인 ───────────────────────────────────────────────────
  primary: {
    ...scale.indigo,
    DEFAULT: scale.indigo[500], // ★ Midnight Indigo. CTA 배경, 학생 데이터 stroke (흰 글자 7.9:1)
    hover: scale.indigo[600],
    active: scale.indigo[700],
    strong: scale.indigo[600], // 진한 채움(진행 바·다크 CTA)
    ink: scale.indigo[600], // 강조 텍스트, 점수
    focus: scale.indigo[400], // focus-visible ring
    soft: scale.indigo[50], // 선택 카드·행 배경
    muted: scale.indigo[100], // 칩 배경, soft hover
    line: scale.indigo[200], // 칩 ring
    "line-strong": scale.indigo[300], // 카드 ring
  },

  // ── 서브 ───────────────────────────────────────────────────
  secondary: {
    ...scale.gray,
    DEFAULT: scale.gray[100], // 서브 버튼 배경
    hover: scale.gray[200],
    active: scale.gray[300],
    soft: scale.gray[50],
    ink: scale.gray[700],
  },

  // ── 강조 (Star Amber — 한 화면에 한 곳) ────────────────────
  accent: {
    ...scale.amber,
    DEFAULT: scale.amber[400], // ★ Star Amber. 배지·진행중 배경 (글자는 text-heading, 흰 글자 금지 2.15:1)
    hover: scale.amber[500],
    active: scale.amber[600],
    soft: scale.amber[100],
    ink: scale.amber[700],
  },

  // ── 오류 ───────────────────────────────────────────────────
  error: {
    ...scale.red,
    DEFAULT: scale.red[500], // 오류 텍스트·삭제 버튼
    hover: scale.red[600],
    active: scale.red[700],
    light: scale.red[400], // weak 배지 채움
    line: scale.red[400], // 입력 오류 border / focus ring
    soft: scale.red[50], // 오류 박스 배경
    ink: scale.red[600], // 12~14px 오류 문구(AA)
  },

  // ── 경고 ───────────────────────────────────────────────────
  warning: {
    ...scale.amber,
    DEFAULT: scale.amber[500], // 난이도 태그·상태 dot 채움. accent(400) 보다 한 단 어둡게 — 같은 앰버 계열이라 명도로만 갈린다
    hover: scale.amber[600],
    soft: scale.amber[50], // 경고 칩·배너 배경
    ink: scale.amber[800], // 경고 칩 텍스트
  },

  // ── 정상 ───────────────────────────────────────────────────
  success: {
    ...scale.green,
    DEFAULT: scale.green[500], // 상태 dot·진행 바
    hover: scale.green[600],
    soft: scale.green[50],
    ink: scale.green[600], // 성공 텍스트
  },
};

/** JS/TS 에서 쓰는 평면 색 객체. `fill={color.primary}`, `stroke={color.line}` */
const color = {
  white,
  heading: semantic.heading,
  strong: semantic.strong,
  body: semantic.body,
  muted: semantic.muted,
  subtle: semantic.subtle,
  disabled: semantic.disabled,
  page: semantic.page,
  fill: semantic.fill.DEFAULT,
  fillStrong: semantic.fill.strong,
  inverse: semantic.inverse,
  lineFaint: semantic.line.faint,
  lineSubtle: semantic.line.subtle,
  line: semantic.line.DEFAULT,
  lineStrong: semantic.line.strong,

  primary: semantic.primary.DEFAULT,
  primaryHover: semantic.primary.hover,
  primaryActive: semantic.primary.active,
  primaryStrong: semantic.primary.strong,
  primaryInk: semantic.primary.ink,
  primaryFocus: semantic.primary.focus,
  primarySoft: semantic.primary.soft,
  primaryMuted: semantic.primary.muted,
  primaryLine: semantic.primary.line,
  primaryLineStrong: semantic.primary["line-strong"],

  secondary: semantic.secondary.DEFAULT,
  secondaryInk: semantic.secondary.ink,

  accent: semantic.accent.DEFAULT,
  accentHover: semantic.accent.hover,
  accentSoft: semantic.accent.soft,
  accentInk: semantic.accent.ink,

  error: semantic.error.DEFAULT,
  errorHover: semantic.error.hover,
  errorLight: semantic.error.light,
  errorLine: semantic.error.line,
  errorSoft: semantic.error.soft,
  errorInk: semantic.error.ink,

  warning: semantic.warning.DEFAULT,
  warningSoft: semantic.warning.soft,
  warningInk: semantic.warning.ink,

  success: semantic.success.DEFAULT,
  successHover: semantic.success.hover,
  successSoft: semantic.success.soft,
  successInk: semantic.success.ink,
};

module.exports = { scale, semantic, color, white };
